
import '../styles/Watermark.css';
import {useEffect, useRef, useCallback, Suspense, forwardRef, useImperativeHandle} from "react";
import * as React from "react";
import Lottie from "lottie-react";
import type CreativeEngine from "@cesdk/engine";

import animation from "../assets/animations/watermark.json";

// Page height (px) at which the watermark is shown at full (natural) size; scales down below this
const WM_FULL_SIZE_PAGE_HEIGHT = 900;

// Positioning padding from page edges (in pixels, scaled by markScale)
const WM_PADDING_LEFT = 20;
const WM_PADDING_BOTTOM = 20;

// Tooltip timing (milliseconds)
const TOOLTIP_AUTO_SHOW_DELAY_MS = 700;
const TOOLTIP_AUTO_HIDE_DELAY_MS = 2000;

interface WatermarkProps {
    senderName?: string;
    senderEmail?: string;
    engine?: CreativeEngine | null;
    activePage?: number | null;
}

export interface WatermarkHandle {
    positionWatermark: () => void;
}

const Watermark = forwardRef<WatermarkHandle, WatermarkProps>(({senderName, senderEmail, engine, activePage}: WatermarkProps, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const markRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const closeTimerRef = useRef<number | null>(null);

    const showTooltip = useCallback(() => {
        const tooltip = tooltipRef.current;
        if (!tooltip) return;
        tooltip.classList.add('wm-open');
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        closeTimerRef.current = window.setTimeout(() => {
            tooltip.classList.remove('wm-open');
            closeTimerRef.current = null;
        }, TOOLTIP_AUTO_HIDE_DELAY_MS);
    }, []);

    // Auto-show tooltip after TOOLTIP_AUTO_SHOW_DELAY_MS on mount
    useEffect(() => {
        const timer = window.setTimeout(showTooltip, TOOLTIP_AUTO_SHOW_DELAY_MS);
        return () => {
            clearTimeout(timer);
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        };
    }, [showTooltip]);

    // Double-click / double-tap detection using document-level listeners
    // (wm-mark has pointer-events:none so clicks pass through to the surface)
    useEffect(() => {
        const mark = markRef.current;
        if (!mark) return;

        const isInsideMark = (x: number, y: number) => {
            const rect = mark.getBoundingClientRect();
            return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        };

        const dblHandler = (e: MouseEvent) => {
            if (isInsideMark(e.clientX, e.clientY)) showTooltip();
        };
        document.addEventListener('dblclick', dblHandler, true);

        // Mobile double-tap via two rapid touchends
        let lastTap = 0;
        let lastTapX = 0;
        let lastTapY = 0;
        const touchHandler = (e: TouchEvent) => {
            const touch = e.changedTouches[0];
            if (!touch) return;
            const now = Date.now();
            if (now - lastTap < 400 && isInsideMark(lastTapX, lastTapY) && isInsideMark(touch.clientX, touch.clientY)) {
                showTooltip();
            }
            lastTap = now;
            lastTapX = touch.clientX;
            lastTapY = touch.clientY;
        };
        document.addEventListener('touchend', touchHandler, true);

        return () => {
            document.removeEventListener('dblclick', dblHandler, true);
            document.removeEventListener('touchend', touchHandler, true);
        };
    }, [showTooltip]);

    const positionWatermark = useCallback((): void => {
        const container = containerRef.current;
        const mark = markRef.current;
        const tooltip = tooltipRef.current;
        if (!container || !mark || !tooltip || !engine || !activePage) return;

        const canvas = engine.element as HTMLCanvasElement | undefined;
        if (!canvas) { container.style.display = 'none'; return; }

        const raw = engine.block.getScreenSpaceBoundingBoxXYWH([activePage]);
        if (!raw) { container.style.display = 'none'; return; }

        const pageX = raw[0];
        const pageY = raw[1];
        const pageWidth = raw[2];
        const pageHeight = raw[3];

        const canvasRect = canvas.getBoundingClientRect();

        // Position container to cover the page area
        const pageLeft = canvasRect.left + pageX;
        const pageTop = canvasRect.top + pageY;

        container.style.display = 'block';
        container.style.left = `${pageLeft}px`;
        container.style.top = `${pageTop}px`;
        container.style.width = `${pageWidth}px`;
        container.style.height = `${pageHeight}px`;

        // Scale the mark – full (natural) size at WM_FULL_SIZE_PAGE_HEIGHT and above, proportionally smaller below
        const markScale = Math.min(1, pageHeight / WM_FULL_SIZE_PAGE_HEIGHT) * 1.2;

        const baseMarkWidth = 150;
        const baseMarkHeight = 100;

        const markWidth = baseMarkWidth * markScale;
        const markHeight = baseMarkHeight * markScale;
        const paddingLeft = WM_PADDING_LEFT * markScale;
        const paddingBottom = WM_PADDING_BOTTOM * markScale;

        mark.style.width = `${markWidth}px`;
        mark.style.height = `${markHeight}px`;
        mark.style.left = `${paddingLeft}px`;
        mark.style.bottom = `${paddingBottom}px`;
        mark.style.top = 'auto';

        // Position tooltip above the mark, bottom-left (fixed size, not scaled)
        tooltip.style.left = `${paddingLeft}px`;
        tooltip.style.bottom = `${paddingBottom + markHeight + 4}px`;
        tooltip.style.top = 'auto';
    }, [engine, activePage]);

    useImperativeHandle(ref, () => ({ positionWatermark }), [positionWatermark]);

    useEffect(() => {
        if (!engine) return;

        const handleResize = () => positionWatermark();
        const handleScroll = () => positionWatermark();

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        const unsubscribeZoom = engine.scene.onZoomLevelChanged(handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
            if (unsubscribeZoom) {
                try { unsubscribeZoom(); } catch {}
            }
        };
    }, [engine, positionWatermark]);

    return (
        <div id="wm-container" ref={containerRef} aria-hidden="true">
            <div id="wm-mark" ref={markRef}>
                {animation && (
                    <Suspense fallback={null}>
                        <Lottie animationData={animation} loop={false}/>
                    </Suspense>
                )}
            </div>
            <div id="wm-tooltip" ref={tooltipRef}><strong>Sent By:</strong>
                {senderName && <><br/>{senderName}</>}
                {senderEmail && <><br/>{senderEmail}</>}
            </div>
        </div>
    );
});

Watermark.displayName = 'Watermark';

export default Watermark;
